/* eslint-disable react/prop-types, no-underscore-dangle */
import React from 'react';

import { Breadcrumb, Button, Grid, Header, Card } from 'semantic-ui-react';

import { graphql } from 'gatsby';
import _isEmpty from 'lodash/isEmpty';

import {
  InternalLink,
  OutboundLink,
  Image,
  SEO,
  MarkdownContent,
  HorizontalImageCard,
  FullImageCard,
  FishermanIcon,
  ServiceCategorySchema,
  BreadcrumbListSchema,
  WebsiteComponentInteraction,
  BasicCarousel,
  componentIterator,
  EVENT_CATEGORIES,
  EVENT_ACTIONS,
  SlideReveal,
  FadeReveal,
  Section,
  PriceRange,
} from '@bluefin/components';

import { createServiceCategorySlug, createServiceItemSlug } from '../../index';
import {
  getBookNowPage,
  getBusinessFiles,
  getFirstBusinessFile,
  getCtaInteractions,
  getRelatedCategories,
  getCategoryItems,
  getComponentContentNodeContent,
} from '../../template-utils';
import { constructFullPageUrl, ensureSlugSlashes, getPageUrl } from '../../utils';

export default function ServiceCategoryTemplate({ data, pageContext }) {
  const {
    fishermanBusinessMenuCategory: businessMenuCategory = {},
    fishermanBusiness: business = {},
    allFishermanBusinessMenuItem: { nodes: allItems = [] },
    allFishermanBusinessMenuCategory: { nodes: allCategories = [] },
    allFishermanBusinessFile: { nodes: allFiles = [] },
    allFishermanBusinessWebsitePage: { nodes: allPages = [] } = {},
    fishermanBusinessWebsitePage: { components: contentNodeComponents = [] } = {},
    site: {
      siteMetadata: { baseUrl },
    },
  } = data;

  const {
    name,
    description,
    fullDescription,
    interactions,
    files,
    seoTitle,
    seoDescription,
    seoOgTitle,
    seoOgDescription,
    seoOgImage,
  } = businessMenuCategory;

  const { name: businessName, businessCategories = [], website } = business;

  const {
    fishermanParentPageTitle = 'Services',
    fishermanParentPageSlug = '/services/',
    templateProps = {},
  } = pageContext;
  const {
    relatedServicesText = 'Related Services',
    servicesText = 'Services',
    showAllRelatedServices = false,
    priceRangeProps = {},
  } = templateProps;

  const url = getPageUrl(website, [
    createServiceCategorySlug(businessMenuCategory, allCategories, fishermanParentPageSlug),
  ]);

  const bookingPage = getBookNowPage(allPages);
  const categoryItems = getCategoryItems(businessMenuCategory, allItems);
  const otherCategories = getRelatedCategories(businessMenuCategory, allCategories, {
    showAllRelatedServices,
  });
  const categoryFiles = getBusinessFiles(files, allFiles);
  const filteredInteractions = getCtaInteractions(interactions);

  const otherServiceHeaderContent = getComponentContentNodeContent({
    components: contentNodeComponents,
    componentId: 'related_services_header',
  });
  const seeAllServicesButtonContent = getComponentContentNodeContent({
    components: contentNodeComponents,
    componentId: 'see_all_services_button',
  });

  return (
    <>
      <SEO
        businessName={businessName}
        pageName={`Services - ${name}`}
        title={seoTitle}
        description={seoDescription || description}
        truncateDescription
        images={categoryFiles}
        ogTitle={seoOgTitle}
        ogDescription={seoOgDescription}
        ogImage={seoOgImage}
        ogUrl={url}
      />
      <ServiceCategorySchema
        businessName={businessName}
        businessCategories={businessCategories}
        name={name}
        description={description}
      />
      <BreadcrumbListSchema
        items={[
          {
            name: `${fishermanParentPageTitle} | ${businessName}`,
            url: constructFullPageUrl(baseUrl, fishermanParentPageSlug),
          },
          { name: `Services - ${name} | ${businessName}` },
        ]}
      />

      <div className="service-category-page-container">
        <Breadcrumb>
          <Breadcrumb.Section
            link
            to={ensureSlugSlashes(fishermanParentPageSlug)}
            as={InternalLink}
          >
            <FishermanIcon iconName="fancy-arrow-left" />
            {fishermanParentPageTitle}
          </Breadcrumb.Section>
          <Breadcrumb.Divider content="/" />
          <Breadcrumb.Section>{name}</Breadcrumb.Section>
        </Breadcrumb>

        <Section className="hero-section">
          <Grid stackable doubling>
            <Grid.Column width={7} textAlign="center" className="information-column">
              <div>
                <Header as="h1">{name}</Header>
                <MarkdownContent className="short-description" content={description} />
                <div className="actions">
                  {bookingPage && _isEmpty(filteredInteractions) && (
                    <Button
                      secondary
                      as={OutboundLink}
                      to={bookingPage.url}
                      event={{
                        category: EVENT_CATEGORIES.BOOKING_EVENT_CATEGORY,
                        action: EVENT_ACTIONS.BOOKING_SCHEDULE_APPOINTMENT_INTENT_EVENT_ACTION,
                      }}
                    >
                      {bookingPage.title}
                    </Button>
                  )}
                  {filteredInteractions.map((interaction) => {
                    return (
                      <WebsiteComponentInteraction
                        key={interaction._id}
                        interaction={interaction}
                        className="service-category-interaction"
                        displayComponentPropsMap={{
                          button: {
                            size: null,
                            compact: false,
                          },
                        }}
                        event={{
                          category: EVENT_CATEGORIES.SERVICES_EVENT_CATEGORY,
                          action: EVENT_ACTIONS.SERVICES_CATEGORY_INTERACTION_ACTION,
                        }}
                      />
                    );
                  })}
                </div>

                <MarkdownContent content={fullDescription} className="full-description" />
              </div>
            </Grid.Column>
            {!_isEmpty(categoryFiles) && (
              <Grid.Column width={9} textAlign="center" className="image-column">
                <FadeReveal triggerOnce>
                  <BasicCarousel
                    className="service-object-images"
                    size={1}
                    grouping={1}
                    displayIndicators={false}
                    displayPlayPauseButton={false}
                    autoScroll={false}
                    leftArrowIcon="fancy-arrow-left"
                    rightArrowIcon="fancy-arrow-right"
                    slide
                    items={componentIterator({
                      iterator: categoryFiles,
                      component: (
                        <Image background className="category-image-item" loadStrategy="eager" />
                      ),
                      propMap: { src: 'src', altText: 'altText' },
                    })}
                  />
                </FadeReveal>
              </Grid.Column>
            )}
          </Grid>
        </Section>

        <Section className="items-section" textAlign="center">
          <Header as="h2">
            {name} {servicesText}
          </Header>
          <Card.Group className="category-items" stackable itemsPerRow={2}>
            {categoryItems.map((item) => {
              const {
                _id: itemId,
                name: itemName,
                description: itemDescription,
                files: itemFiles,
                variations,
              } = item;

              const itemImage = getFirstBusinessFile(itemFiles, allFiles);
              const { src: itemImageSrc, altText: itemImageAlt } = itemImage || {};

              return (
                <FadeReveal key={`${itemId}_${itemName}`} className="ui card" triggerOnce>
                  <HorizontalImageCard
                    as={InternalLink}
                    to={createServiceItemSlug(item, allItems, allCategories)}
                    imagePosition="right"
                    image={itemImageSrc}
                    event={{
                      category: EVENT_CATEGORIES.SERVICES_EVENT_CATEGORY,
                      action: EVENT_ACTIONS.SERVICES_VIEW_ITEM_DETAILS_EVENT_ACTION,
                      label: itemName,
                    }}
                    link
                    altText={itemImageAlt}
                  >
                    <Card.Header className="header-container">
                      <Card.Header> {itemName}</Card.Header>
                      <PriceRange
                        priceKey="price"
                        settings={{
                          displayType: 'range',
                          displayZeroCost: false,
                        }}
                        prices={variations}
                        {...(priceRangeProps || {})}
                      />
                    </Card.Header>
                    <MarkdownContent content={itemDescription} />
                  </HorizontalImageCard>
                </FadeReveal>
              );
            })}
          </Card.Group>
        </Section>

        {!_isEmpty(otherCategories) && (
          <Section className="related-services-section" textAlign="center">
            <Header as="h2">{otherServiceHeaderContent || relatedServicesText}</Header>
            <Card.Group itemsPerRow={3}>
              {otherCategories.map((category) => {
                const {
                  _id: relatedCategoryId,
                  name: relatedCategoryName,
                  files: relatedCategoryFiles,
                } = category;

                const relatedCategoryImage = getFirstBusinessFile(relatedCategoryFiles, allFiles);
                const { src: relatedCategoryImageSrc, altText: relatedCategoryImageAlt } =
                  relatedCategoryImage || {};

                return (
                  <SlideReveal
                    key={relatedCategoryId}
                    triggerOnce
                    direction="up"
                    fade
                    className="ui card"
                  >
                    <FullImageCard
                      as={InternalLink}
                      to={createServiceCategorySlug(category, allCategories)}
                      link
                      image={relatedCategoryImageSrc}
                      altText={relatedCategoryImageAlt}
                      event={{
                        category: EVENT_CATEGORIES.SERVICES_EVENT_CATEGORY,
                        action: EVENT_ACTIONS.SERVICES_VIEW_CATEGORY_DETAILS_EVENT_ACTION,
                        label: relatedCategoryName,
                      }}
                    >
                      <Header>{relatedCategoryName}</Header>
                      <FishermanIcon iconName="fancy-arrow-right" />
                    </FullImageCard>
                  </SlideReveal>
                );
              })}
            </Card.Group>
            <InternalLink
              to={ensureSlugSlashes(fishermanParentPageSlug)}
              label="See All Services"
              event={{
                category: EVENT_CATEGORIES.SERVICES_EVENT_CATEGORY,
                action: EVENT_ACTIONS.SERVICES_VIEW_ALL_SERVICES_EVENT_ACTION,
              }}
              className="ui button basic"
            >
              <span>{seeAllServicesButtonContent || 'See All Services'}</span>
              <FishermanIcon iconName="fancy-arrow-right" />
            </InternalLink>
          </Section>
        )}
      </div>
    </>
  );
}

export const query = graphql`
  query ($id: String!) {
    site {
      siteMetadata {
        baseUrl
      }
    }
    allFishermanBusinessWebsitePage {
      nodes {
        url
        title
        pageType
      }
    }
    fishermanBusinessWebsitePage(pageType: { eq: "Services" }) {
      components {
        componentIdentifier
        contentNodes {
          content
        }
      }
    }
    fishermanBusiness {
      _id
      name
      type
      businessCategories {
        category
      }
      website {
        domains {
          url
          primaryDomain
        }
      }
    }
    allFishermanBusinessMenuCategory(sort: { order: ASC, fields: order }) {
      nodes {
        _id
        name
        items
        files
        schedule {
          _id
        }
      }
    }
    fishermanBusinessMenuCategory(_id: { eq: $id }) {
      items
      description
      fullDescription
      files
      interactions {
        _id
        displayType
        behaviorType
        url
        enabled
        style
        label
      }
      name
      _id
      seoTitle
      seoDescription
      schedule {
        _id
      }
      seoOgTitle
      seoOgDescription
      seoOgImage {
        url
        alt
        type
      }
    }
    allFishermanBusinessFile {
      nodes {
        _id
        file
        gatsbyFile {
          childImageSharp {
            gatsbyImageData(layout: FULL_WIDTH)
          }
        }
        description
      }
    }
    allFishermanBusinessMenuItem(
      filter: { visible: { eq: true } }
      sort: { order: ASC, fields: order }
    ) {
      nodes {
        _id
        description
        name
        annotations {
          likeCount
        }
        variations {
          _id
          menuItem
          name
          order
          price
        }
        visible
        available
        files
      }
    }
  }
`;
